import React, { useState, useEffect } from 'react';
import { 
  CheckCircleIcon, 
  ClockIcon, 
  UserIcon, 
  CalendarIcon,
  ExclamationTriangleIcon,
  PlusIcon 
} from '@heroicons/react/24/outline';
import FollowUpService from '../services/followUpService';

const FollowUpHistory = ({ leadId, refreshTrigger = 0 }) => {
  const [followUps, setFollowUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leadId) {
      fetchFollowUps();
    }
  }, [leadId, refreshTrigger]);

  const fetchFollowUps = async () => {
    try {
      setLoading(true);
      const result = await FollowUpService.getLeadFollowUps(leadId);
      
      if (result.success) {
        const formattedFollowUps = result.data.followUps.map(followUp => 
          FollowUpService.formatFollowUpForDisplay(followUp)
        );
        setFollowUps(formattedFollowUps);
      } else {
        setError(result.message);
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
      setError('Failed to load follow-up history');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkComplete = async (followUpId, completed) => {
    try {
      const result = await FollowUpService.updateFollowUp(leadId, followUpId, { completed });
      
      if (result.success) {
        // Update local state
        setFollowUps(prev => prev.map(followUp => 
          followUp._id === followUpId 
            ? { ...followUp, completed }
            : followUp
        ));
      }
    } catch (error) {
      console.error('Error updating follow-up:', error);
    }
  };

  const getFollowUpStatusIcon = (followUp) => {
    if (followUp.completed) {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    }
    
    if (followUp.nextFollowUpDate) {
      const nextDate = new Date(followUp.nextFollowUpDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (nextDate < today) {
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      }
    }
    
    return <ClockIcon className="h-5 w-5 text-yellow-500" />;
  };

  const getFollowUpStatusText = (followUp) => {
    if (followUp.completed) {
      return { text: 'Completed', color: 'text-green-600' };
    }
    
    if (followUp.nextFollowUpDate) {
      const nextDate = new Date(followUp.nextFollowUpDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (nextDate < today) {
        const daysOverdue = Math.floor((today - nextDate) / (1000 * 60 * 60 * 24));
        return { 
          text: `Overdue by ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`, 
          color: 'text-red-600' 
        };
      }
      
      return { text: 'Pending', color: 'text-yellow-600' };
    }
    
    return { text: 'Pending', color: 'text-yellow-600' };
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-red-600">
          <ExclamationTriangleIcon className="h-8 w-8 mx-auto mb-2" />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2 text-blue-600" />
            Follow-up History ({followUps.length})
          </h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">
              {followUps.filter(f => f.completed).length} completed
            </span>
            <span className="text-sm text-gray-600">•</span>
            <span className="text-sm text-gray-600">
              {followUps.filter(f => !f.completed).length} pending
            </span>
          </div>
        </div>
      </div>

      <div className="p-6">
        {followUps.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-gray-100 rounded-full p-4 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
              <ClockIcon className="h-10 w-10 text-gray-400" />
            </div>
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Follow-ups Yet</h4>
            <p className="text-gray-500 mb-4">Start tracking your lead interactions by adding follow-ups</p>
            <button className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add First Follow-up
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {followUps.map((followUp, index) => {
              const statusInfo = getFollowUpStatusText(followUp);
              
              return (
                <div 
                  key={followUp._id || index}
                  className={`border rounded-xl p-5 transition-all shadow-sm hover:shadow-md ${
                    followUp.completed 
                      ? 'border-green-200 bg-gradient-to-r from-green-50 to-green-100' 
                      : statusInfo.color === 'text-red-600'
                      ? 'border-red-200 bg-gradient-to-r from-red-50 to-red-100'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="flex-shrink-0 mt-1">
                        {getFollowUpStatusIcon(followUp)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="flex-shrink-0">
                              <span className="text-2xl">{followUp.typeIcon}</span>
                            </div>
                            <div>
                              <h4 className="text-base font-semibold text-gray-900">
                                {followUp.summary}
                              </h4>
                              <div className="flex items-center space-x-2 mt-1">
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${followUp.priorityColor}`}>
                                  {followUp.priority}
                                </span>
                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                  followUp.completed 
                                    ? 'bg-green-100 text-green-800'
                                    : statusInfo.color === 'text-red-600'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {statusInfo.text}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {followUp.notes && (
                          <div className="bg-gray-50 rounded-lg p-3 mb-3">
                            <p className="text-sm text-gray-700 leading-relaxed">
                              {followUp.notes}
                            </p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                          <div className="flex items-center text-gray-600">
                            <CalendarIcon className="h-4 w-4 mr-2 text-blue-500" />
                            <span className="font-medium">Date:</span>
                            <span className="ml-1">{followUp.formattedDate}</span>
                          </div>
                          
                          {followUp.addedByName && (
                            <div className="flex items-center text-gray-600">
                              <UserIcon className="h-4 w-4 mr-2 text-purple-500" />
                              <span className="font-medium">By:</span>
                              <span className="ml-1">{followUp.addedByName}</span>
                              <span className="text-gray-400 ml-1">({followUp.addedByRole})</span>
                            </div>
                          )}
                          
                          <div className="flex items-center">
                            <div className={`flex items-center ${statusInfo.color}`}>
                              {getFollowUpStatusIcon(followUp)}
                              <span className="ml-2 font-medium">{statusInfo.text}</span>
                            </div>
                          </div>
                        </div>
                        
                        {followUp.nextFollowUpDate && (
                          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center text-blue-800">
                              <ClockIcon className="h-4 w-4 mr-2" />
                              <span className="font-semibold">Next Follow-up:</span>
                              <span className="ml-2">{followUp.formattedNextDate}</span>
                            </div>
                            {followUp.nextStep && (
                              <div className="mt-2 text-blue-700">
                                <span className="font-semibold">Next Step:</span>
                                <span className="ml-2">{followUp.nextStep}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 mt-4">
                      {!followUp.completed && (
                        <button
                          onClick={() => handleMarkComplete(followUp._id, true)}
                          className="inline-flex items-center px-3 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                        >
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Mark Complete
                        </button>
                      )}
                      {followUp.completed && (
                        <div className="inline-flex items-center px-3 py-2 bg-green-100 text-green-800 text-sm font-medium rounded-lg">
                          <CheckCircleIcon className="h-4 w-4 mr-2" />
                          Completed
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FollowUpHistory;
